import { JEFE_3 } from '../../../data/boss-objects';
import { PLAYER_HITBOX, SIM_HZ } from '../../../data/tuning';
import { createAabb } from '../../collision';
import { createCropDuster, dusterX, stepCropDuster } from '../../enemies/crop-duster';
import { createSombreroThrower, stepSombreroThrower } from '../../enemies/sombrero';
import { clearPhaseObjects, spawnObject, ticksOf, type AttackObject, type PatternEnv } from '../attacks';
import { cellX, cellY, colOfX, createPiece, stepPiece, type PieceKind } from '../chess';
import type { BossFight } from '../boss-fight';
import { registerPattern } from '../patterns';

/**
 * Attack patterns of JEFE-3 El Padrinito (GDD §9.2, issue #121). Each is called once per tick while its phase is active
 * and the boss is not in transition, so nothing moves, rings or closes during one. Entering a phase clears what the
 * previous one left: the pieces, the minibosses called by the phone and the check columns.
 */

function syncPhase(fight: BossFight, env: PatternEnv): void {
  const { attacks } = env;
  if (attacks.phase !== fight.phase) {
    // The first call of the fight (phase 0) has nothing to clear; later ones clear what the phase before left.
    if (attacks.phase !== 0) clearPhaseObjects(attacks);
    attacks.phase = fight.phase;
    attacks.cadence = 0;
    attacks.pending = [];
    attacks.phaseTicks = 0;
    attacks.lastCentre = null;
    attacks.checks = 0;
  }
  attacks.phaseTicks += 1;
}

const piecesOf = (objects: readonly AttackObject[]) => objects.filter((object) => object.piece);

/** Phase 1, "Plazas": pieces appear at the right edge and move one cell every second, announced half a second before. */
registerPattern('plazas', (fight, context, env) => {
  if (!env) return;
  syncPhase(fight, env);
  const { attacks, view } = env;
  const pieces = piecesOf(attacks.objects);
  const occupied = (row: number, col: number) => pieces.some((object) => object.piece!.row === row && object.piece!.col === col);

  for (const object of pieces) {
    const piece = object.piece!;
    const step = stepPiece(piece, (row, col) => occupied(row, col));
    if (step.announced) context.emit({ type: 'boss:piece-announce', kind: piece.kind, row: piece.next!.row, col: piece.next!.col });
    if (step.moved) {
      object.x = cellX(piece.col);
      object.y = cellY(piece.row);
      context.emit({ type: 'boss:piece-move', kind: piece.kind, row: piece.row, col: piece.col });
    }
  }

  // A new piece every 2 s while the board has room: at most 3 on screen, so a board column always keeps a free row.
  attacks.cadence += 1;
  const onScreen = pieces.filter((object) => object.x > view.left - 2 && object.x < view.right + 2).length;
  if (attacks.cadence < ticksOf(JEFE_3.pieceEverySeconds) || onScreen >= JEFE_3.maxPieces) return;
  attacks.cadence = 0;
  const col = colOfX(view.right + JEFE_3.cellSize);
  const free = Array.from({ length: JEFE_3.rows }, (_, row) => row).filter((row) => !occupied(row, col));
  if (free.length === 0) return;
  const kind: PieceKind = attacks.pieces % 2 === 0 ? 'rook' : 'bishop';
  const row = free[Math.floor(attacks.rng() * free.length)]!;
  const dr = attacks.rng() < 0.5 ? 1 : -1;
  const dc = attacks.rng() < 0.5 ? 1 : -1;
  attacks.pieces += 1;
  const piece = createPiece(kind, row, col, dr, dc);
  spawnObject(attacks, context, 'chess-piece', { x: cellX(col), y: cellY(row), piece });
});

/** Ticks from the transition to each ring of the phone and from a ring to the miniboss it calls. */
const FIRST_RING = ticksOf(JEFE_3.firstRingSeconds);
const RING_EVERY = ticksOf(JEFE_3.ringEverySeconds);
const SUMMON_DELAY = ticksOf(JEFE_3.summonDelaySeconds);

/** Phase 2, "Llamadas": every ring of the phone calls, alternating, a crop duster (EN-31) or a sombrero thrower (EN-32). */
registerPattern('llamadas', (fight, context, env) => {
  if (!env) return;
  syncPhase(fight, env);
  const { attacks, run, view } = env;
  const ticks = attacks.phaseTicks;
  // The phone rings at 4 s and every 5 s from then on; the miniboss arrives 1 s after each ring.
  if (ticks >= FIRST_RING && (ticks - FIRST_RING) % RING_EVERY === 0) {
    attacks.rings.push(run.core.tick);
    context.emit({ type: 'boss:phone-ring', ring: attacks.rings.length });
  }
  const sinceFirst = ticks - FIRST_RING - SUMMON_DELAY;
  if (sinceFirst >= 0 && sinceFirst % RING_EVERY === 0) {
    const call = Math.floor(sinceFirst / RING_EVERY);
    if (call % 2 === 0) {
      const startX = view.right + JEFE_3.dusterEnterBeyond;
      attacks.summoned.push({ type: 'EN-31', startX, x: startX, y: JEFE_3.dusterY, duster: createCropDuster(), thrower: null });
    } else {
      const startX = view.right - JEFE_3.balconyFromRight;
      attacks.summoned.push({ type: 'EN-32', startX, x: startX, y: JEFE_3.balconyY, duster: null, thrower: createSombreroThrower() });
    }
    context.emit({ type: 'boss:summon', enemyType: attacks.summoned[attacks.summoned.length - 1]!.type });
  }
  // The called minibosses run their own rules: the crop duster flies with the plane and sprays while on screen, the
  // thrower throws while its balcony is ahead of the plane (with the Soborno it looks away).
  const plane = createAabb(run.core.player.x, run.core.player.y, PLAYER_HITBOX.width, PLAYER_HITBOX.height);
  for (const enemy of attacks.summoned) {
    if (enemy.duster) {
      enemy.x = dusterX(enemy.startX, enemy.duster.ticks + 1);
      const onScreen = enemy.x >= view.left && enemy.x <= view.right;
      if (stepCropDuster(enemy.duster, enemy, onScreen)) context.emit({ type: 'en31:spray', summoned: true });
    }
    if (enemy.thrower) {
      const onScreen = enemy.x >= view.left && enemy.x <= view.right;
      const events = stepSombreroThrower(enemy.thrower, enemy, run.core.player.y, onScreen && enemy.x > plane.x, run.core.bribeRemaining > 0);
      if (events.windup) context.emit({ type: 'sombrero:windup', summoned: true });
      if (events.thrown) context.emit({ type: 'sombrero:thrown', summoned: true });
    }
  }
});

/** Phase 3, "Jaque": a column of two pieces every 8 u whose gap closes with the phase down to 3 u and stays there. */
registerPattern('jaque', (fight, context, env) => {
  if (!env) return;
  syncPhase(fight, env);
  const { attacks, run, view } = env;
  const every = Math.round((JEFE_3.checkSpacing / run.scrollSpeed) * SIM_HZ);
  attacks.cadence += 1;
  if (attacks.checks > 0 && attacks.cadence < every) return;
  attacks.cadence = 0;
  // The centre moves at most checkMaxStep from the previous column, inside the band that keeps the gap reachable.
  const previous = attacks.lastCentre ?? (JEFE_3.checkCentreLow + JEFE_3.checkCentreHigh) / 2;
  const wanted = JEFE_3.checkCentreLow + attacks.rng() * (JEFE_3.checkCentreHigh - JEFE_3.checkCentreLow);
  const centre = Math.min(JEFE_3.checkCentreHigh, Math.max(JEFE_3.checkCentreLow, Math.min(previous + JEFE_3.checkMaxStep, Math.max(previous - JEFE_3.checkMaxStep, wanted))));
  attacks.lastCentre = centre;
  attacks.checks += 1;
  spawnObject(attacks, context, 'check-column', { x: view.right + JEFE_3.checkWidth, y: 6, check: { centre } });
});
